// "No insurance on file in Athena" warning (owner 2026-10-15).
//
// Athena refuses to check a visit in without an insurance on the patient - a self-pay patient
// needs the "*SELF PAY*" package added in athenaNet - and a visit that is not checked in has no
// encounter the doctor's note can be signed on. Three 9928 visits were stuck that way since 09-01.
// The front desk is warned BEFORE / AT check-in, from our own copy of the appointment (local data
// only, never an Athena call): a small chip on the dashboard card (./insuranceWarningChip.jsx, in
// place of the insurance name) and a line in the dialogs that check the patient in: Visit Status
// (a check-in status mints the queue token and fires the Athena check-in) and the Orders screen
// (its first Save of the day is orders/confirm, the same). Not in Purpose of visit (review 10-15):
// its save sends purpose values, and both gateways check in there only for "In-office - Checked
// In" / "Ready for Triage", which are no purpose. It never blocks anything.
//
// The signal: the appointment row's insurancename is blank. The booking writers (care scheduler,
// app, kiosk) and the refresh syncs copy the patient's Athena insurance onto the row when they
// create it, and the syncs fill or update it from Athena later but never clear it (a blank answer
// from Athena keeps our copy), so blank = Athena had no insurance at the last read (or the row was
// never read). Measured 09-15..10-15 (9928): 32 blank of 4,062 appointments, 20 of them cancelled
// / no-show; 2 of the 12 held ones were still checked in on Athena (the false alarms; both carry
// the Athena encounter id, none of the 5 refused / stuck blank visits does); Athena's insurance
// refusals since 09-01: 3 of 4 on blank rows. Local data cannot tell "added in athenaNet since the
// last sync" from "none on file", so the chip has one wording and the tooltip says it follows the
// sync.
//
// Not flagged:
//   - patient-only cards (no appointment id: the dashboard's patient_master fallback row carries
//     a literal "" insurancename);
//   - visits no longer held (Cancelled / Rescheduled / No Show);
//   - past days (nothing left to check in);
//   - a row without an insurancename field (unknown, not blank);
//   - a visit Athena already checked in: the row carries an Athena encounter id (Athena opens the
//     encounter at check-in; v2's own "checked in" test, athena_checkin._checked_in_patients - on
//     a dashboard card it may be a same-day sibling's, the patient was checked in that day all
//     the same), today's v2 check-in list says checked_in, or the row's signintime.AthenaCheckin
//     stamp does (rarely written: 0 history rows dated since 10-01): the insurance was there, our
//     copy is behind.
import { hasAppointmentId, visitNotHeld } from "../../utils/visitGuard";
import { practiceFeatures } from "../../config/practice";
import { clinicTodayKey, parseJourney, startDateKey } from "./visitflow";
import { useAthenaCheckinOutcomes } from "./athenaCheckin";

export const NO_INSURANCE_LABEL = "No insurance on file";
export const NO_INSURANCE_NOTICE =
  "No insurance on file in Athena. Add the patient's insurance — or *SELF PAY* — in athenaNet and check the visit in there, or the doctor can't sign the note.";
export const NO_INSURANCE_SYNC_NOTE =
  "Read from our copy of the appointment: insurance added in athenaNet since the last sync from Athena shows here after the next one.";

const blank = (v) => v === null || String(v).trim() === "";

// An Athena encounter id ("0" / "" / non-digits are none).
const hasEncounter = (v) => /^\d+$/.test(String(v ?? "").trim()) && Number(v) > 0;

const checkedInOnAthena = (row, outcomes) => {
  if (hasEncounter(row.encounterid)) return true;
  const o = (outcomes || {})[String(row.appointmentid)];
  if (o && o.status === "checked_in") return true;
  const stamp = parseJourney(row.signintime).AthenaCheckin;
  return Boolean(stamp && typeof stamp === "object" && stamp.status === "checked_in");
};

// {label, notice, title} when the visit has no insurance on file, else null.
// outcomes: today's v2 check-in list by appointment id ({} for other days).
export const insuranceWarning = (row, outcomes = {}, now = new Date()) => {
  if (!row || typeof row !== "object") return null;
  if (!hasAppointmentId(row.appointmentid) || visitNotHeld(row)) return null;
  if (!Object.prototype.hasOwnProperty.call(row, "insurancename") || !blank(row.insurancename)) return null;
  const day = startDateKey(row.startdate);
  if (!day || day < clinicTodayKey(now)) return null;
  if (checkedInOnAthena(row, outcomes)) return null;
  return {
    label: NO_INSURANCE_LABEL,
    notice: NO_INSURANCE_NOTICE,
    title: `${NO_INSURANCE_NOTICE}\n${NO_INSURANCE_SYNC_NOTE}`,
  };
};

// The warning for a card / dialog row (null where the practice does not use it). Today's v2
// check-in list is the one the card's Athena check-in chip loads (shared, throttled); nothing is
// loaded for a practice without the warning.
export const useInsuranceWarning = (row) => {
  const on = Boolean(practiceFeatures().noInsuranceWarning);
  const outcomes = useAthenaCheckinOutcomes(row, { load: on });
  return on ? insuranceWarning(row, outcomes) : null;
};
